/**
 * TouchHandler - Advanced touch support for mobile
 * Includes haptic feedback, swipe gestures, and long-press detection
 */

export interface TouchGestureConfig {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onLongPress?: () => void;
  onDoubleTap?: () => void;
}

export class TouchHandler {
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private lastTapTime = 0;
  private longPressTimer: number | null = null;
  private minSwipeDistance = 50;
  private longPressDuration = 500;
  private doubleTapDelay = 300;

  constructor(private element: HTMLElement, private config: TouchGestureConfig) {
    this.init();
  }

  private init() {
    this.element.addEventListener('touchstart', (e) => this.handleTouchStart(e), false);
    this.element.addEventListener('touchmove', () => this.handleTouchMove(), false);
    this.element.addEventListener('touchend', (e) => this.handleTouchEnd(e), false);
    this.element.addEventListener('touchcancel', () => this.handleTouchCancel(), false);
  }

  private handleTouchStart(e: TouchEvent) {
    const touch = e.touches[0];
    if (!touch) return;

    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();

    // Set up long press detection
    this.longPressTimer = window.setTimeout(() => {
      this.config.onLongPress?.();
      this.triggerHaptic('medium');
    }, this.longPressDuration);

    // Check for double tap
    const now = Date.now();
    if (now - this.lastTapTime < this.doubleTapDelay) {
      this.config.onDoubleTap?.();
      this.triggerHaptic('light');
      this.lastTapTime = 0; // Reset to prevent triple tap
    } else {
      this.lastTapTime = now;
    }
  }

  private handleTouchMove() {
    // Cancel long press if user moves
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  private handleTouchEnd(e: TouchEvent) {
    // Cancel long press timer
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    const touch = e.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const duration = Date.now() - this.touchStartTime;

    // Only detect swipe if it's a quick gesture
    if (duration < 500 && distance > this.minSwipeDistance) {
      const angle = Math.atan2(deltaY, deltaX);
      const angleDegrees = (angle * 180) / Math.PI;

      // Determine swipe direction (with 45-degree tolerance)
      if (angleDegrees > -45 && angleDegrees < 45) {
        // Right swipe
        this.config.onSwipeRight?.();
        this.triggerHaptic('light');
      } else if (angleDegrees > 45 && angleDegrees < 135) {
        // Down swipe
        this.config.onSwipeDown?.();
        this.triggerHaptic('light');
      } else if (angleDegrees > 135 || angleDegrees < -135) {
        // Left swipe
        this.config.onSwipeLeft?.();
        this.triggerHaptic('light');
      } else if (angleDegrees > -135 && angleDegrees < -45) {
        // Up swipe
        this.config.onSwipeUp?.();
        this.triggerHaptic('light');
      }
    }
  }

  private handleTouchCancel() {
    // Cancel long press timer
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  /**
   * Trigger haptic feedback on supported devices
   * Gracefully degrades on unsupported devices
   */
  public triggerHaptic(patternType: 'light' | 'medium' | 'heavy' = 'light') {
    // Check if Vibration API is supported
    if (!navigator.vibrate) return;

    const patterns: Record<string, number | number[]> = {
      light: 10,
      medium: [20, 10, 20],
      heavy: [30, 20, 30],
    };

    const patternValue = patterns[patternType];
    if (patternValue !== undefined) {
      navigator.vibrate(patternValue);
    }
  }

  /**
   * Destroy touch handler and remove event listeners
   */
  public destroy() {
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
    }
    // Note: In a real implementation, you'd remove event listeners
    // For now, they'll be cleaned up when the element is removed
  }
}

/**
 * Add haptic feedback to buttons
 */
export function addHapticToButtons() {
  const buttons = document.querySelectorAll('button');
  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      if (navigator.vibrate) {
        navigator.vibrate(10); // Light haptic on click
      }
    });
  });
}

/**
 * Create a tooltip that appears on long press
 */
export function createLongPressTooltip(element: HTMLElement, text: string) {
  let tooltip: HTMLElement | null = null;

  element.addEventListener('touchstart', () => {
    const timer = setTimeout(() => {
      tooltip = document.createElement('div');
      tooltip.className = 'long-press-tooltip';
      tooltip.textContent = text;
      tooltip.style.cssText = `
        position: fixed;
        background: var(--accent-cyan);
        color: var(--bg-primary);
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 12px;
        pointer-events: none;
        z-index: 10000;
        white-space: nowrap;
      `;

      const rect = element.getBoundingClientRect();
      tooltip.style.left = rect.left + rect.width / 2 - 50 + 'px';
      tooltip.style.top = rect.top - 40 + 'px';

      document.body.appendChild(tooltip);
    }, 500);

    element.addEventListener('touchend', () => {
      clearTimeout(timer);
      if (tooltip) {
        tooltip.remove();
        tooltip = null;
      }
    });

    element.addEventListener('touchcancel', () => {
      clearTimeout(timer);
      if (tooltip) {
        tooltip.remove();
        tooltip = null;
      }
    });
  });
}
